import { type ID, ProjectActions } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions, selectArtboardMode } from '@store/reducers/artboard'
import { managerActions } from '@store/reducers/manager/actions'
import type { AppState } from '@store/types'
import * as ActionTypes from '@storeOld/actions'
import {
  all,
  call,
  debounce,
  race,
  select,
  take,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects'

import { PERMISSION_TYPE } from '@/permissions'
import { GA4_PERMISSION } from '@/permissions/project/definitions'
import { Ga4Actions, InternalGa4Actions } from '../actions'
import { ga4Selectors } from '../selectors'
import { callbackFailed } from './callbackFailed'
import { complete } from './complete'
import { connect } from './connect'
import { disconnect } from './disconnect'
import { fetchReport } from './fetchReport'
import { fetchProperties } from './properties'
import { resync } from './resync'
import { selectProperty } from './selectProperty'
import { trackMetricsView } from './trackMetricsView'

// Оба входа в SEO-режим — SeoActions.enableMode и тумблер тулбара — в итоге
// диспатчат changeMode('seo_mode') (enableMode-сага делает это сама), поэтому
// один watcher здесь покрывает все пути входа.
function* onModeChange({
  payload,
}: ReturnType<typeof artBoardActions.changeMode>) {
  if (payload === 'seo_mode') {
    yield call(fetchReport)
    // Показ цифр считаем на входе в режим, а не на каждый перезапрос отчёта
    // (правка адреса, Resync) — иначе одно открытие дало бы десяток событий.
    yield call(trackMetricsView)
  }
}

// Адрес страницы — единственный ключ, по которому сходятся метрики, поэтому
// после его правки соответствие надо пересобрать. Отчёт на сервере закеширован,
// так что это дешёвый повторный джойн, без обращения к Google. Правки прилетают
// на каждый символ (SeoActions.change), поэтому ждём паузы.
const URL_EDIT_PAUSE = 1200

interface CommitAction {
  type: string
  payload?: unknown[]
}

const isNodeUrlCommit = (action: CommitAction): boolean =>
  action.type === ActionTypes.COMMIT_PROJECT_REQUEST &&
  Array.isArray(action.payload) &&
  action.payload.some(
    (item) =>
      (item as CommitAction | null)?.type ===
      ProjectActions.nodes.updateUrl.type
  )

function* onNodeUrlChange() {
  const mode: string = yield select(selectArtboardMode)
  const connected: boolean = yield select(ga4Selectors.getConnected)

  // Вне SEO-режима цифр на экране нет, а при следующем входе отчёт запросится
  // сам — гонять запрос сейчас незачем.
  if (mode !== 'seo_mode' || !connected) {
    return
  }

  yield call(fetchReport)
}

// Подключение есть, проперти не выбрана — владельцу сразу нужен список для
// пикера в GaMetricsPanel; остальным список не положен (бэкенд ответит 403).
function* onReportLoaded({
  payload,
}: ReturnType<typeof InternalGa4Actions.reportLoaded>) {
  if (payload.status !== 'no_property') {
    return
  }

  const canManage: boolean = yield select(
    (state: AppState) =>
      state.user.permissions?.[GA4_PERMISSION.MANAGE]?.type ===
      PERMISSION_TYPE.YES
  )

  if (canManage) {
    yield call(fetchProperties)
  }
}

function* watchGa4() {
  yield all([
    takeEvery(artBoardActions.changeMode, onModeChange),
    debounce(URL_EDIT_PAUSE, isNodeUrlCommit, onNodeUrlChange),
    takeEvery(InternalGa4Actions.reportLoaded, onReportLoaded),
    takeLeading(Ga4Actions.fetchReport, fetchReport),
    takeLeading(Ga4Actions.connect, connect),
    takeLeading(Ga4Actions.complete, complete),
    takeLeading(Ga4Actions.callbackFailed, callbackFailed),
    takeLeading(Ga4Actions.disconnect, disconnect),
    takeLeading(Ga4Actions.fetchProperties, fetchProperties),
    takeLeading(Ga4Actions.selectProperty, selectProperty),
    takeLeading(Ga4Actions.resync, resync),
  ])
}

function* waitForProjectChange() {
  while (true) {
    const previousId: ID | null = yield select(
      migrationProjectSelector.getProjectId
    )
    const action: { type: string } = yield take('*')
    const projectId: ID | null = yield select(
      migrationProjectSelector.getProjectId
    )

    // The OAuth callback starts before the first project loads. Keep that
    // work alive on initial load, but cancel it when leaving a known project.
    if (
      action.type === managerActions.resetProject.type ||
      (previousId !== null && previousId !== projectId)
    ) {
      return
    }
  }
}

export function* ga4Saga() {
  while (true) {
    // Cancels all pending GA4 work, including nested refreshes and debounce,
    // so late responses cannot repopulate analytics after a project switch.
    yield race({
      changed: call(waitForProjectChange),
      watching: call(watchGa4),
    })
  }
}
